import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import QualityCheckForm from '@/components/manufacturing/QualityCheckForm';
import ManufacturingService from '@/api/manufacturing.service';
import { canEdit } from '@/lib/permissions';

interface QualityCheck {
  id: number;
  publicId: string;
  businessId: number;
  checkNumber: string;
  productionJobId: number;
  productionJobNumber: string;
  recipeName: string;
  checkType: 'incoming' | 'in_process' | 'final' | 'random';
  status: string;
  inspector?: string;
  checkDate: string;
  sampleSize: number;
  passedCount: number;
  failedCount: number;
  defectTypes?: string[];
  notes?: string;
  corrective_action?: string;
  dateCreated: string;
  dateUpdated: string;
}

const EditQualityCheck: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [qualityCheck, setQualityCheck] = useState<QualityCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    if (id) {
      fetchQualityCheck();
    }
  }, [id]);

  const fetchQualityCheck = async () => {
    try {
      setLoading(true);
      const response = await ManufacturingService.getQualityCheck(id!);
      
      if (response.data.result === 'success') {
        setQualityCheck(response.data.qualityCheck);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch quality check",
          variant: "destructive",
        });
        navigate('/manufacturing/quality-control');
      }
    } catch (error) {
      console.error('Error fetching quality check:', error);
      toast({
        title: "Error",
        description: "Failed to fetch quality check",
        variant: "destructive",
      });
      navigate('/manufacturing/quality-control');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    if (!qualityCheck) return;

    try {
      setIsSubmitting(true);

      const updateData = {
        checkNumber: data.checkNumber,
        productionJobId: data.productionJobId,
        checkType: data.checkType,
        inspector: data.inspector || '',
        checkDate: data.checkDate.toISOString(),
        sampleSize: data.sampleSize,
        notes: data.notes || '',
        inspectionCriteria: data.inspectionCriteria,
        defectTypes: data.defectTypes
      };

      const response = await ManufacturingService.updateQualityCheck(qualityCheck.publicId, updateData);

      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Quality check updated successfully",
        });
        navigate('/manufacturing/quality-control');
      } else {
        throw new Error(response.data.message || 'Failed to update quality check');
      }
    } catch (error: any) {
      console.error('Error updating quality check:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update quality check",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/manufacturing/quality-control');
  };

  // Check permissions
  if (!canEdit('Manufacturing')) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Permission Denied</h3>
              <p className="text-muted-foreground">
                You don't have permission to edit quality checks.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Loading quality check...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!qualityCheck) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Quality Check Not Found</h3>
              <p className="text-muted-foreground">
                The quality check you're looking for doesn't exist.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if quality check can be edited (only pending inspections)
  if (qualityCheck.status !== 'pending') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/quality-control')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quality Control
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Cannot Edit Quality Check</h3>
              <p className="text-muted-foreground">
                Only pending quality checks can be edited. This check is {qualityCheck.status}.
              </p>
              <Button
                className="mt-4"
                onClick={() => navigate(`/manufacturing/quality-control/${qualityCheck.publicId}`)}
              >
                View Quality Check
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/manufacturing/quality-control')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Quality Control
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Edit Quality Check</h1>
          <p className="text-muted-foreground">
            Modify quality check: {qualityCheck.checkNumber}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Quality Check Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <QualityCheckForm
            mode="edit"
            initialData={{
              ...qualityCheck,
              checkDate: qualityCheck.checkDate,
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isSubmitting}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default EditQualityCheck; 